import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ProjectListItem from './ProjectListItem';

const ITEMS_PER_PAGE = 4;

const ProjectList = ({ projects, onSelect, userType }) => {
  const [filters, setFilters] = useState({
    Saved: false,
    Submitted: false,
    Withdrawn: false,
    Cancelled: false,
    YetToSubmit: false,
    Accepted: false,
    InReview: false,
    Rejected: false,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const formatLabel = (label) => label.replace(/([a-z])([A-Z])/g, '$1 $2');

  const getColor = (label) => {
    switch (label) {
      // Per UX: Saved + YetToSubmit filters should use the same light red as Submitted.
      case 'Saved': return '#732f2f'; // submitted light red
      case 'Published':
      case 'Submitted': return '#732f2f'; // primary-brand-light
      case 'Withdrawn':
      case 'Cancelled': return '#3c0000'; // primary-brand-dark
      case 'Closed': return '#500000'; // primary-brand
      case 'YetToSubmit':
      case 'Yet To Submit':
      case 'Yet to Submit': return '#732f2f'; // submitted light red
      case 'In Review': return '#3e3e3e'; // gray-800
      // Per UX: every active option should turn red.
      case 'Accepted': return '#732f2f'; // submitted light red
      case 'Rejected': return '#3c0000'; // primary-brand-dark
      default: return '#707070'; // gray-500
    }
  };

  const projectStatusMapping = {
    Saved: 'Saved',
    Published: 'Submitted',
    Cancelled: 'Withdrawn',
    Closed: 'Closed',
  };

  const proposalStatusMapping = {
    Saved: 'Saved',
    Submitted: 'Submitted',
    Withdrawn: 'Withdrawn',
    Closed: 'Withdrawn',
    YetToSubmit: 'YetToSubmit',
  };

  const applicationStatusMapping = {
    Accepted: 'Accepted',
    InReview: 'In Review',
    Rejected: 'Rejected',
  };

  const toggleFilter = (label, isApplicationFilter = false) => {
    let status;
    if (userType === 'Employer') {
      status = projectStatusMapping[label];
    } else if (isApplicationFilter) {
      status = applicationStatusMapping[label];
    } else {
      status = proposalStatusMapping[label];
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [status]: !prevFilters[status],
    }));
    setCurrentPage(1);
  };

  const filteredProjects = projects.filter((project) => {
    if (!Object.values(filters).some((val) => val)) return true;
    if (userType === 'Employer') {
      return filters[project.projectStatus];
    } else {
      const normalizedProposalStatus = project.proposalStatus === 'Closed' ? 'Withdrawn' : project.proposalStatus;
      const hasProposal = normalizedProposalStatus !== null;
      const isProposalFiltered = filters[normalizedProposalStatus] || (!hasProposal && filters['YetToSubmit']);
      const isApplicationFiltered = filters[project.applicationStatus] && normalizedProposalStatus === 'Submitted';

      return isProposalFiltered || isApplicationFiltered;
    }
  });

  const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column">
      {/* Fixed Header and Filters */}
      <Box padding={2} borderBottom="1px solid #ccc" bgcolor="#e9e4dc" position="sticky" top={0} zIndex={2}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Projects
        </Typography>

        {userType === 'Employer' && (
          <>
            <Typography variant="subtitle1" marginBottom={1}>Project Status</Typography>
            <Box display="flex" gap={1} marginBottom={1} flexWrap="nowrap">
              {Object.keys(projectStatusMapping).map((label) => (
                <Button
                  key={label}
                  variant="contained"
                  size="small"
                  onClick={() => toggleFilter(label)}
                  style={{
                    backgroundColor: filters[projectStatusMapping[label]] ? getColor(label) : '#707070',
                    color: '#ffffff',
                    fontWeight: 'bold',
                  }}
                >
                  {filters[projectStatusMapping[label]] ? '✖' : '＋'} {label.toUpperCase()}
                </Button>
              ))}
            </Box>
          </>
        )}

        {userType === 'Student' && (
          <>
            <Typography variant="subtitle1" marginBottom={1}>Proposal Status</Typography>
            <Box display="flex" gap={1} marginBottom={1} flexWrap="nowrap">
              {Object.keys(proposalStatusMapping)
                .filter((label) => label !== 'Closed')
                .map((label) => (
                <Button
                  key={label}
                  variant="contained"
                  size="small"
                  onClick={() => toggleFilter(label)}
                  style={{
                    backgroundColor: filters[proposalStatusMapping[label]] ? getColor(label) : '#707070',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',  // Reduced font size
                    padding: '4px 8px',  // Adjust padding for better fit
                    minWidth: 'auto' // Allows buttons to shrink dynamically
                  }}
                >
                  {filters[proposalStatusMapping[label]] ? '✖' : '＋'} {formatLabel(label).toUpperCase()}
                </Button>
              ))}
            </Box>

            <Box display="flex" gap={1} marginBottom={1} flexWrap="nowrap">
              {Object.keys(applicationStatusMapping).map((label) => (
                <Button
                  key={label}
                  variant="contained"
                  size="small"
                  onClick={() => toggleFilter(label, true)}
                  style={{
                    backgroundColor: filters[applicationStatusMapping[label]] ? getColor(label) : '#707070',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',  // Reduced font size
                    padding: '4px 8px',  // Adjust padding for better fit
                    minWidth: 'auto' // Allows buttons to shrink dynamically
                  }}
                >
                  {filters[applicationStatusMapping[label]] ? '✖' : '＋'} {formatLabel(label).toUpperCase()}
                </Button>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Scrollable Project List */}
      <Box flexGrow={1} overflow="auto" padding={2} minHeight={0}>
        {paginatedProjects.map((project) => (
          <Box key={project.projectID} borderBottom="1px solid #ccc" paddingY={0} marginBottom={0}>
            <ProjectListItem
              project={project}
              onSelect={onSelect}
              userType={userType}
              proposalStatus={project.proposalStatus}
              applicationStatus={project.applicationStatus}
            />
          </Box>
        ))}
      </Box>

      {/* Fixed Pagination Controls */}
      <Box padding={2} borderTop="1px solid #ccc" bgcolor="#e9e4dc" position="sticky" bottom={0} zIndex={2}>
        <Box display="flex" justifyContent="center" alignItems="center" marginTop={1} paddingTop={0}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            style={{ marginRight: '8px' }}
          >
            Previous
          </Button>
          <Typography variant="body1">
            {totalPages === 0 ? 'No projects to display' : `Page ${currentPage} of ${totalPages}`}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            style={{ marginLeft: '8px' }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectList;
